import { Color } from './java/Color';
import { Graphics2D } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';
import { TrailPool } from './TrailPool';
import { GamePalette } from './GamePalette';
import { EnemyBossBullet } from './EnemyBossBullet';
import { Game } from './Game';

export class PlayerProjectile extends GameObject {
  private static readonly SIZE = 10;

  private static readonly CORE = new Color(230, 234, 240);

  private handler: Handler;
  private damage: number;
  private trailTick = 0;
  private readonly boundsRect = new Rectangle();

  constructor(
    x: number,
    y: number,
    id: ID,
    handler: Handler,
    vx: number,
    vy: number,
    damage: number,
  ) {
    super(x, y, id);
    this.handler = handler;
    this.damage = damage;
    this.velX = vx;
    this.velY = vy;
  }

  getBounds(): Rectangle {
    this.boundsRect.x = Math.trunc(this.x);
    this.boundsRect.y = Math.trunc(this.y);
    this.boundsRect.width = PlayerProjectile.SIZE;
    this.boundsRect.height = PlayerProjectile.SIZE;
    return this.boundsRect;
  }

  tick(): void {
    this.x += this.velX;
    this.y += this.velY;

    // Remove if off-screen
    if (this.x < -50 || this.x > Game.WIDTH + 50 || this.y < -50 || this.y > Game.HEIGHT + 50) {
      this.handler.removeObject(this);
      return;
    }

    // Trail
    if (++this.trailTick % 2 === 0) {
      TrailPool.add(
        this.x,
        this.y,
        GamePalette.accent(),
        PlayerProjectile.SIZE,
        PlayerProjectile.SIZE,
        0.04,
        TrailPool.SHAPE_CIRCLE,
      );
    }

    // Collision with enemies
    this.checkCollision();
  }

  private checkCollision(): void {
    const objects: GameObject[] = this.handler.getObjects();
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      const oid = obj.getId();
      // Only hit enemies
      if (
        oid !== ID.BasicEnemy &&
        oid !== ID.FastEnemy &&
        oid !== ID.SmartEnemy &&
        oid !== ID.HardEnemy &&
        oid !== ID.EnemyBoss
      )
        continue;
      // Skip other projectiles (EnemyBossBullet uses ID.BasicEnemy but isn't a real enemy)
      if (obj instanceof EnemyBossBullet) continue;
      if (obj instanceof PlayerProjectile) continue;

      if (this.getBounds().intersects(obj.getBounds())) {
        if (obj.takeDamage(this.damage)) {
          // Spawn hit particles
          this.spawnHitParticles(obj);
          this.handler.removeObject(this);
          return;
        }
      }
    }
  }

  private spawnHitParticles(target: GameObject): void {
    const accent = GamePalette.accent();
    const cx = target.getX() + 16;
    const cy = target.getY() + 16;
    for (let i = 0; i < 4; i++) {
      const angle = Math.random() * Math.PI * 2;
      TrailPool.add(
        cx + Math.cos(angle) * 8,
        cy + Math.sin(angle) * 8,
        accent,
        6,
        6,
        0.06,
        TrailPool.SHAPE_CIRCLE,
      );
    }
  }

  render(g: Graphics2D): void {
    const g2 = g;
    const ix = Math.trunc(this.x);
    const iy = Math.trunc(this.y);
    const accent = GamePalette.accent();

    // Outer glow
    g2.setColor(new Color(accent.getRed(), accent.getGreen(), accent.getBlue(), 50));
    g2.fillOval(ix - 3, iy - 3, PlayerProjectile.SIZE + 6, PlayerProjectile.SIZE + 6);
    // Core
    g2.setColor(PlayerProjectile.CORE);
    g2.fillOval(ix, iy, PlayerProjectile.SIZE, PlayerProjectile.SIZE);
    // Inner accent
    g2.setColor(accent);
    g2.fillOval(ix + 2, iy + 2, PlayerProjectile.SIZE - 4, PlayerProjectile.SIZE - 4);
  }
}
